import { i18nMessage } from "@/lib/i18nMessage";
import { FedexResolvedAddress } from "@/types/api/api-types";

export type ComparableAddressSnapshot = {
  street: string;
  additionalInfo: string;
  city: string;
  stateOrProvinceCode: string;
  postalCode: string;
  countryCode: string;
};

export type FedexAddressComparisonRow = {
  /** Message i18n (`i18nMessage`), à traduire au rendu avec `useTranslateMessage`. */
  label: string;
  original: string;
  suggested: string;
  highlightSuggested: boolean;
};

export function normalizeAddressPart(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

export function resolvedAddressToSnapshot(
  resolved: FedexResolvedAddress,
): ComparableAddressSnapshot {
  const lines = resolved.streetLinesToken?.filter((line) => line.trim()) ?? [];
  const postalFromParsed = [resolved.parsedPostalCode?.base, resolved.parsedPostalCode?.addOn]
    .filter(Boolean)
    .join("-");
  const postal = resolved.postalCodeToken?.value?.trim() || postalFromParsed || "";

  return {
    street: lines[0] ?? "",
    additionalInfo: lines.slice(1).join(", "),
    city: resolved.city ?? "",
    stateOrProvinceCode: resolved.stateOrProvinceCode ?? "",
    postalCode: postal,
    countryCode: resolved.countryCode ?? "",
  };
}

export function shouldShowStateOrProvince(countryCode: string): boolean {
  return countryCode === "CA" || countryCode === "US";
}

export function buildFedexAddressComparisonRows(
  original: ComparableAddressSnapshot,
  resolved: FedexResolvedAddress,
  treatCountryAsFixed?: boolean,
  showStateOrProvince = true,
): FedexAddressComparisonRow[] {
  const suggested = resolvedAddressToSnapshot(resolved);
  const rows: Array<[string, string, string]> = [
    [
      i18nMessage("boExpeditionForm:fedexAddressComparison.address1Label"),
      original.street,
      suggested.street,
    ],
    [
      i18nMessage("boExpeditionForm:fedexAddressComparison.address2Label"),
      original.additionalInfo,
      suggested.additionalInfo,
    ],
    [
      i18nMessage("boExpeditionForm:fedexAddressComparison.cityLabel"),
      original.city,
      suggested.city,
    ],
  ];

  if (showStateOrProvince) {
    rows.push([
      i18nMessage("boExpeditionForm:fedexAddressComparison.stateOrProvinceLabel"),
      original.stateOrProvinceCode,
      suggested.stateOrProvinceCode,
    ]);
  }

  const countryLabel = i18nMessage("boExpeditionForm:fedexAddressComparison.countryLabel");
  rows.push(
    [
      i18nMessage("boExpeditionForm:fedexAddressComparison.postalCodeLabel"),
      original.postalCode,
      suggested.postalCode,
    ],
    [countryLabel, original.countryCode, suggested.countryCode],
  );
  return rows.map(([label, o, s]) => ({
    label,
    original: o.trim() ? o : "—",
    suggested: s.trim() ? s : "—",
    highlightSuggested:
      !(treatCountryAsFixed && label === countryLabel) &&
      normalizeAddressPart(o) !== normalizeAddressPart(s),
  }));
}
